import React from "react";
import { without } from "lodash-es";

import { useLocales } from "hooks";

import { SubscriptionsList } from "./SubscriptionsList";
import { Subscription, useSubscriptions } from "./useSubscriptions";
import { usePush } from "./usePush";
import { SubscriptionForm, Values } from "./SubscriptionForm";
import { PushWidget } from "./PushWidget";

export interface StationPageWidgetProps {
  publicKey: string;
  stationKey: string;
  metricLabel: string;
}

export const StationPageWidget: React.FC<StationPageWidgetProps> = ({
  publicKey,
  stationKey,
  metricLabel,
}) => {
  const { locale, t } = useLocales();

  const {
    serviceWorkersAvailable,
    pushSubscription,
    permissionState,
    askForPermission,
  } = usePush({ publicKey });
  const pushEnabled = Boolean(pushSubscription);

  const {
    subscriptions,
    status,
    refresh,
    destroy,
    subscriptionIdsBeingDeleted,
    create,
  } = useSubscriptions({
    enabled: pushEnabled,
    pushSubscription,
    stationKey,
  });

  const handleDelete = (subscription: Subscription) => {
    destroy(subscription)
      .catch(() => alert(t("deleteError")))
      .finally(refresh);
  };

  const handleCreate = (values: Values) => {
    return create({
      station_key: stationKey,
      condition: values.comparison,
      threshold: values.threshold,
    })
      .catch(() => alert(t("createError")))
      .finally(refresh);
  };

  const availableComparisons = without(
    ["exceeds", "deceeds"],
    ...(subscriptions || []).map((subscription) => subscription.condition)
  );

  const isLoading =
    !pushSubscription || status === "loading" || status === "idle";
  const hasSubscriptions =
    typeof subscriptions !== "undefined" && subscriptions.length !== 0;

  return (
    <div className="row mt-6">
      <div className="col-xs-12 col-md-8">
        <h2>{t("station_page_widget.form_title")}</h2>
        <p>{t("station_page_widget.description")}</p>
        <PushWidget
          serviceWorkersAvailable={serviceWorkersAvailable}
          permissionState={permissionState}
          askForPermission={askForPermission}
        />
        {pushEnabled && isLoading && <p>{t("loading")}</p>}
        {pushEnabled && !isLoading && (
          <React.Fragment>
            {availableComparisons.length > 0 ? (
              <SubscriptionForm
                availableComparisons={availableComparisons}
                onCreate={handleCreate}
                metricLabel={metricLabel}
              />
            ) : (
              <div className="alert alert-info" role="alert">
                {t("station_page_widget.noComparisonsAvailable")}
              </div>
            )}
            <a href={`/${locale}/subscriptions`}>
              {t("subscriptionsPageLink")}
            </a>
          </React.Fragment>
        )}
      </div>
      <div className="col-xs-12 col-md-4">
        {pushEnabled && !isLoading && hasSubscriptions && (
          <React.Fragment>
            <h3>{t("station_page_widget.list_title")}</h3>
            <SubscriptionsList
              subscriptions={subscriptions}
              subscriptionIdsBeingDeleted={subscriptionIdsBeingDeleted}
              handleDelete={handleDelete}
            />
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
